import React, { useEffect, useState, useRef } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { isShoppingMode } from '../../config/appMode';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import { canCancelBid, getBidCancelDisabledReason } from '../../utils/bidCancelUtils';
import { parseApiDateTime } from '../../utils/dateUtils';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import PrimaryButton from '../../components/PrimaryButton';
import NoDataContainer from '../../components/NoDataContainer';
import DataLoader from '../../components/DataLoader';
import Modal from '../../components/Modal';
import { FormTitle } from '../../customComponents/FormTitle';
import LotCard from '../../customComponents/FavoriteCard';
import ShoppingCard from '../../customComponents/ShoppingCard';
import { getAuction } from '../../lib/api/user/userApi';
import { likeLot } from '../../lib/api/user/userApi';
import { cancelPreBid } from '../../lib/api/user/userApi';
import { AppColors } from '../../styles/colors';

const LotDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// LOT 카드 래퍼: scrollIntoView 시 상단 고정 헤더에 가려지지 않도록 여백을 줌
const LotCardWrapper = styled.div`
  scroll-margin-top: 120px; // ProjectAppbar 높이에 맞춰 조정 가능
`;

// 즐겨찾기 카드 컨테이너 (그리드) - Favorites와 동일
const LotDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px 0;
  max-width: 100%;

  & > * {
    min-width: 0;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 32px;
    padding: 32px 0;
  }
`;

// 웹 전용 타이틀 행 (FormTitle과 라이브보기 버튼을 space-between으로 배치)
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`;

const LiveViewButton = styled.button`
  width: 89px;
  height: 28px;
  background-color: #a6d4d1;
  border: none;
  border-radius: 8px;
  color: #262626;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  margin-top:32px;

  &:hover {
    opacity: 0.8;
  }
`;

// 모달 버튼 스타일 (로그아웃 모달과 동일)
const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const CancelModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: ${AppColors.btnC};
  color: ${AppColors.onBtnC};
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const ConfirmModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: ${AppColors.btnCEmphasis};
  color: ${AppColors.onBtnCEmphasis};
  
  &:hover {
    background-color: #262626;
    color: #FFFFFF;
  }
`;

// 앱 전용 라이브보기 버튼 컨테이너
const MobileLiveButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0 0 16px 0;
`;

// 즐겨찾기 아이템 인터페이스 - ListSection의 AuctionItem과 호환
interface FavoriteItem {
  id: number;
  imageUrl: string;
  auctionHouse: string;
  auctioneerIndex: number; // auctioneerIndex 필드 추가
  auction: string;
  lotName: string;
  isFavorite: boolean;
  liveTime?: string;
  isLive: boolean;
  photoSubmissionDate: string; // 사전입찰 시작 시간
  auctionStartDate: string; // 경매 시작 시간
  auctionEndDate: string; // 경매 종료 시간
  isEnded: boolean; // 경매 종료 여부
  liveStartPrice: number; // 라이브 시작가 (숫자)
  startPrice: number; // 시작가 (숫자)
  liveEndTime: string; // 라이브 종료 시간
  startingPrice?: string; // ListSection의 AuctionItem과 호환을 위해 추가
  winningPrice?: string; // 낙찰가
  likedUserIndexes?: number[]; // 좋아요한 사용자 인덱스 배열
  // 원본 데이터 포함
  [key: string]: any; // 다른 원본 필드들을 위한 인덱스 시그니처
}

// Props 인터페이스 정의
interface LotDetailProps {
  onLikeClick?: (e: React.MouseEvent, lotId: number, currentLikedUserIndexes?: number[]) => Promise<void>;
  favoriteManager?: any;
}

// 더미 즐겨찾기 데이터를 제거하고 API에서 받은 데이터를 사용합니다

// 이미지 URL 처리 함수
const getProfileImageSrc = (profileImageUrl?: string | null) => {
  if (!profileImageUrl || profileImageUrl.trim() === "") {
    return "/common/nodata.svg";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
  if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
    return profileImageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (profileImageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
};

// 다국어 텍스트 - Favorites와 동일
const favoritesTexts = {
  ko: {
    title: 'LOT 내역',
    lotDetailTitle: 'LOT 내역',
    preBid: isShoppingMode() ? '구매하기' : '입찰하기',
    preSubmission: isShoppingMode() ? '구매하기' : '사전 입찰하기',
    liveView: '라이브보기',
    liveBid: isShoppingMode() ? '구매하기' : '라이브입찰하기',
    cancelBid: isShoppingMode() ? '구매취소' : '입찰취소',
    cancelBidBlockedTitle: isShoppingMode() ? '구매취소 불가' : '입찰취소 불가',
    emptyTitle: '즐겨찾기가 비어있어요',
    emptyDescription: '관심있는 작품을 즐겨찾기에 추가해보세요.',
    photoSubmissionDate: isShoppingMode() ? '판매시작' : '사전입찰',
    auctionStart: isShoppingMode() ? '판매시작' : '경매시작',
    auctionEnded: isShoppingMode() ? '판매종료' : '경매종료',
    auctionNotStarted: isShoppingMode() ? '판매 시작 전입니다' : '경매 시작 전입니다',
    auctionEndedTitle: isShoppingMode() ? '판매가 종료되었습니다' : '경매가 종료되었습니다',
    auctionEndedMessage: isShoppingMode() ? '이 판매는 이미 종료되었습니다.' : '이 경매는 이미 종료되었습니다.',
    confirmButton: '확인',
    liveStartPrice: isShoppingMode() ? '판매가' : '라이브 시작가',
    startPrice: isShoppingMode() ? '판매가' : '시작가',
    purchase: isShoppingMode() ? '구매하기' : '입찰하기',
    cancelBidModalTitle: isShoppingMode() ? '구매를 취소하시겠습니까?' : '입찰을 취소하시겠습니까?',
    cancelBidModalMessage: isShoppingMode() ? '취소 후에는 해당 구매 내역이 무효 처리되며, 다시 복구할 수 없습니다.' : '취소 후에는 해당 입찰 내역이 무효 처리되며, 다시 복구할 수 없습니다.',
    cancel: '취소',
    confirmCancel: isShoppingMode() ? '구매취소' : '입찰취소',
    topPreBidder: 'Winning',
    secondPreBidder: 'Loosing'
  },
  en: {
    title: 'LOT History',
    lotDetailTitle: 'LOT HISTORY',
    preBid: isShoppingMode() ? 'Purchase' : 'Bid',
    preSubmission: isShoppingMode() ? 'Purchase' : 'Pre-Bid',
    liveView: 'Live View',
    liveBid: isShoppingMode() ? 'Purchase' : 'Live Bid',
    cancelBid: isShoppingMode() ? 'Cancel Purchase' : 'Cancel Bid',
    cancelBidBlockedTitle: isShoppingMode() ? 'Cancellation Unavailable' : 'Cancellation Unavailable',
    emptyTitle: 'No favorites yet',
    emptyDescription: 'Add artworks you\'re interested in to your favorites.',
    photoSubmissionDate: isShoppingMode() ? 'Sale Start' : 'Pre-Bid',
    auctionStart: isShoppingMode() ? 'Sale Start' : 'Auction Start',
    auctionEnded: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    auctionNotStarted: isShoppingMode() ? 'Sale Not Started' : 'Auction Not Started',
    auctionEndedTitle: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    auctionEndedMessage: isShoppingMode() ? 'This sale has already ended.' : 'This auction has already ended.',
    confirmButton: 'OK',
    liveStartPrice: isShoppingMode() ? 'Price' : 'Live Starting Price',
    startPrice: isShoppingMode() ? 'Price' : 'Starting Price',
    purchase: isShoppingMode() ? 'Purchase' : 'Bid',
    cancelBidModalTitle: isShoppingMode() ? 'Do you want to cancel your purchase?' : 'Do you want to cancel your bid?',
    cancelBidModalMessage: isShoppingMode() ? 'After cancellation, your purchase will be invalidated and cannot be recovered.' : 'After cancellation, your bid will be invalidated and cannot be recovered.',
    cancel: 'Cancel',
    confirmCancel: isShoppingMode() ? 'Cancel Purchase' : 'Cancel Bid',
    topPreBidder: 'Winning',
    secondPreBidder: 'Loosing'
  }
};

// 요일 다국어 텍스트
const dayNamesLocalized = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

// LiveEndTime 포맷팅 함수 (07/17(금) 13:00 종료 / Jul 17(Fri) 13:00 Ends)
const formatLiveEndTime = (dateStr: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const date = new Date(dateStr);
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mm
    const day = date.getDate(); // d (패딩 없음)
    const hours = date.getHours().toString().padStart(2, '0'); // hh
    const minutes = date.getMinutes().toString().padStart(2, '0'); // MM
    const dayName = dayNamesLocalized[language][date.getDay()]; // 요일
    
    if (language === 'ko') {
      return `${month}/${day}(${dayName}) ${hours}:${minutes} 종료`;
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[date.getMonth()];
      return `${monthName} ${day}(${dayName}) ${hours}:${minutes} Ends`;
    }
  } catch (error) {
    return dateStr;
  }
};

// 날짜를 로컬 형식으로 포맷팅하는 함수
const formatDateToLocalString = (date: Date, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const year = date.getFullYear().toString().slice(-2); // yy
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mm
    const day = date.getDate().toString().padStart(2, '0'); // dd
    const hours = date.getHours().toString().padStart(2, '0'); // hh
    const minutes = date.getMinutes().toString().padStart(2, '0'); // MM
    const dayName = dayNamesLocalized[language][date.getDay()]; // 요일
    
    return language === 'ko' 
      ? `${year}.${month}.${day} (${dayName}) ${hours}:${minutes}`
      : `${hours}:${minutes} on ${month}/${day}/${year} (${dayName})`;
  } catch (error) {
    return date.toString();
  }
};

// KRW 가격 포맷팅 함수 (숫자를 KRW 1,000,000 형태로 변환) - 국제화 지원
const formatPrice = (price: number, language: 'ko' | 'en' = 'ko', exchangeRate: number = 1300): string => {
  if (language === 'ko') {
    return `KRW ${price.toLocaleString()}`;
  } else {
    const usdPrice = price / exchangeRate;
    return `USD ${usdPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

// preBids에서 특정 lotIndex에 대한 최고 가격 계산하는 함수
const getHighestPreBidPrice = (preBids: any[], lotIndex: number): number => {
  if (!preBids || preBids.length === 0) return 0;
  
  const lotPreBids = preBids.filter(bid => bid.lotIndex === lotIndex);
  if (lotPreBids.length === 0) return 0;
  
  return Math.max(...lotPreBids.map(bid => bid.price || 0));
};

// 경매 상태 확인 함수 - 실제 API 데이터 구조에 맞춤
// preBidEndAt은 사용하지 않고, "사전입찰 시작 여부"와 "라이브 시작 여부"만 기준으로 판단
const getAuctionStatus = (item: any): 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended' => {
  // 먼저 개별 LOT의 ended 상태 확인
  const lotData = (item as any).apiData;
  if (lotData?.ended === true) {
    return 'ended';
  }

  const now = new Date();
  
  // auction 객체에서 시간 정보 추출 (화면/컴포넌트에 따라 shape가 다를 수 있음)
  const auction = (item as any).apiData?.auction ?? (item as any).auction;
  if (!auction) return 'ended';
  
  // 시간 정보들을 Date 객체로 변환
  const preBidStartAt = parseApiDateTime(auction.preBidStartAt);
  const auctionStartAt = parseApiDateTime(auction.auctionStartAt);
  const auctionEndAt = parseApiDateTime(auction.auctionEndAt);

  if (!preBidStartAt || !auctionStartAt || !auctionEndAt) return 'ended';
  
  // 1) 경매 종료
  if (now > auctionEndAt) return 'ended';

  // 2) 라이브 진행 여부는 항상 auctionStartAt 기준
  if (now >= auctionStartAt && now <= auctionEndAt) {
    return 'live';
  }

  // 3) 사전입찰 시작 전
  if (now < preBidStartAt) return 'before-submission';

  // 4) 사전입찰 기간 (preBidStartAt ~ auctionStartAt 직전까지)
  if (now >= preBidStartAt && now < auctionStartAt) {
    return 'submission-period';
  }

  // 기타 경우는 종료로 처리
  return 'ended';
};

function LotDetail({ onLikeClick, favoriteManager }: LotDetailProps = {}) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useUserAuth();
  const { lang } = useLang();
  const texts = favoritesTexts[lang];

  // 스크롤 참조를 위한 ref 객체
  const lotCardRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [auctionData, setAuctionData] = useState<any>(null);
  const [favoriteItems, setFavoriteItems] = useState<FavoriteItem[]>([]);
  const [exchangeRate] = useState(1300); // 환율 기본값

  // 모달 상태 관리
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    buttonText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    buttonText: ''
  });

  // 입찰 취소 모달 상태 관리
  const [cancelBidModalState, setCancelBidModalState] = useState<{
    isOpen: boolean;
    selectedItem: FavoriteItem | null;
    mode: 'confirm' | 'blocked';
    blockedReason: string;
  }>({
    isOpen: false,
    selectedItem: null,
    mode: 'confirm',
    blockedReason: ''
  });

  // 사용자가 해당 LOT에 사전입찰을 했는지 확인하는 함수
  const hasUserPreBid = (item: FavoriteItem): boolean => {
    if (!user?.index || !(auctionData as any)?.preBids) return false;
    
    return (auctionData as any).preBids.some((bid: any) => 
      bid.user.index === user.index && bid.lotIndex === item.id
    );
  };

  // 해당 LOT에서 사용자의 사전입찰 순위를 계산하는 함수 (1위, 2위 등)
  const getUserPreBidRank = (item: FavoriteItem): number | null => {
    if (!user?.index || !(auctionData as any)?.preBids) return null;

    const preBidsForLot = (auctionData as any).preBids.filter((bid: any) => bid.lotIndex === item.id && bid.user?.index);
    if (!preBidsForLot.length) return null;

    const userMaxPriceMap = new Map<number, number>();

    preBidsForLot.forEach((bid: any) => {
      const userIndex = bid.user.index as number;
      const price = bid.price || 0;
      const prev = userMaxPriceMap.get(userIndex);
      if (prev === undefined || price > prev) {
        userMaxPriceMap.set(userIndex, price);
      }
    });

    if (!userMaxPriceMap.size) return null;

    const sorted = Array.from(userMaxPriceMap.entries()).sort((a, b) => b[1] - a[1]);
    const rankIndex = sorted.findIndex(([userIndex]) => userIndex === user.index);

    if (rankIndex === -1) return null;

    return rankIndex + 1; // 0-based -> 1위, 2위 ...
  };

  // 사용자가 해당 LOT의 최고 사전 입찰자인지 확인하는 함수 (1위)
  const isUserTopPreBidder = (item: FavoriteItem): boolean => {
    return getUserPreBidRank(item) === 1;
  };

  // 사용자가 해당 LOT의 차순위(2위) 사전 입찰자인지 확인하는 함수
  const isUserSecondPreBidder = (item: FavoriteItem): boolean => {
    return getUserPreBidRank(item) === 2;
  };

  // 입찰 취소 핸들러
  const handleCancelBid = (item: any) => {
    // 입찰 취소 가능 여부 확인
    const auction = (item as any).apiData?.auction ?? (item as any).auction;
    const auctionStatus = getAuctionStatus(item);
    
    if (!auction?.auctionStartAt || !canCancelBid(auction.auctionStartAt, auctionStatus)) {
      const disabledReason = getBidCancelDisabledReason(auction?.auctionStartAt || '', auctionStatus, lang);
      setCancelBidModalState({
        isOpen: true,
        selectedItem: null,
        mode: 'blocked',
        blockedReason: disabledReason
      });
      return;
    }

    setCancelBidModalState({
      isOpen: true,
      selectedItem: item,
      mode: 'confirm',
      blockedReason: ''
    });
  };

  // 입찰 취소 확인 핸들러
  const handleConfirmCancelBid = async () => {
    if (!cancelBidModalState.selectedItem) return;
    
    try {
      devLog('입찰 취소 API 호출 시작:', cancelBidModalState.selectedItem);
      
      // LOT index만 확인 (정확한 값만 사용)
      const selectedItem = cancelBidModalState.selectedItem;
      const lotId = (selectedItem as any).index;
      
      devLog('추출된 lotId:', lotId);
      
      if (!lotId) {
        devError('LOT index를 찾을 수 없습니다:', selectedItem);
        return;
      }
      
      // 추가 방어 로직: API 호출 직전에 다시 한 번 확인
      const auction = (selectedItem as any).apiData?.auction ?? (selectedItem as any).auction;
      const auctionStatus = getAuctionStatus(selectedItem);
      
      if (!auction?.auctionStartAt || !canCancelBid(auction.auctionStartAt, auctionStatus)) {
        const disabledReason = getBidCancelDisabledReason(auction?.auctionStartAt || '', auctionStatus, lang);

        // 확인 직전에 조건이 바뀐 경우(시간 경과 등) 사유 모달로 전환
        setCancelBidModalState({
          isOpen: true,
          selectedItem: null,
          mode: 'blocked',
          blockedReason: disabledReason
        });
        return;
      }
      
      // 실제 API 호출로 입찰 취소 처리
      const response = await cancelPreBid(lotId);
      
      devLog('입찰 취소 API 응답:', response);
      
      if (response.data.statusCode === 200) {
        devLog('입찰 취소 성공');
        
        // 모달 닫기
        setCancelBidModalState({
          isOpen: false,
          selectedItem: null,
          mode: 'confirm',
          blockedReason: ''
        });
        
        // 페이지 새로고침 또는 데이터 다시 로드
        window.location.reload();
      } else {
        devError('입찰 취소 실패:', response.data);
        // 실패 시 에러 메시지 표시 (선택사항)
        setModalState({
          isOpen: true,
          title: '입찰 취소 실패',
          message: response.data.message || '입찰 취소 중 오류가 발생했습니다.',
          buttonText: '확인'
        });
        
        // 입찰 취소 모달 닫기
        setCancelBidModalState({
          isOpen: false,
          selectedItem: null,
          mode: 'confirm',
          blockedReason: ''
        });
      }
    } catch (error) {
      devError('입찰 취소 API 호출 중 오류:', error);
      
      // 에러 시 메시지 표시
      setModalState({
        isOpen: true,
        title: '입찰 취소 실패',
        message: '네트워크 오류가 발생했습니다. 다시 시도해주세요.',
        buttonText: '확인'
      });
      
      // 입찰 취소 모달 닫기
      setCancelBidModalState({
        isOpen: false,
        selectedItem: null,
        mode: 'confirm',
        blockedReason: ''
      });
    }
  };

  // 입찰 취소 모달 닫기
  const handleCancelBidModalClose = () => {
    setCancelBidModalState({
      isOpen: false,
      selectedItem: null,
      mode: 'confirm',
      blockedReason: ''
    });
  };

  // 즐겨찾기 업데이트 이벤트 핸들러
  useEffect(() => {
    if (favoriteManager) {
      const cleanup = favoriteManager.onFavoriteUpdate((event: any) => {
        setFavoriteItems(prev => 
          prev.map(item => 
            item.id === event.lotId
              ? { ...item, likedUserIndexes: event.likedUserIndexes }
              : item
          )
        );
      });

      return cleanup; // cleanup 함수 반환
    }
  }, [favoriteManager]);

  // 경매 데이터 로드
  useEffect(() => {
    const loadAuctionData = async () => {
      if (!id) {
        navigate('/');
        return;
      }

      try {
        setIsLoading(true);
        const response = await getAuction(id);
        
        if (response.data.statusCode === 200 && response.data.data) {
          const auction = response.data.data;
          setAuctionData(auction);
          
          // lots 데이터를 FavoriteItem 형태로 변환
          if (auction.lots && auction.lots.length > 0) {
            const convertedItems = auction.lots.map((lot: any) => ({
              id: lot.index,
              imageUrl: lot.images?.[0] ? getProfileImageSrc(lot.images[0]) : getProfileImageSrc(''),
              auctionHouse: auction.auctioneer?.name || '',
              auctioneerIndex: auction.auctioneer?.index || 0, // auctioneerIndex 추가
              auction: auction.name || '',
              lotName: lot.lotName || lot.name,
              isFavorite: false, // TODO: 실제 좋아요 상태 확인
              liveTime: auction.auctionEndAt,
              isLive: false, // TODO: 실제 라이브 상태 확인
              photoSubmissionDate: auction.preBidStartAt,
              auctionStartDate: auction.auctionStartAt,
              auctionEndDate: auction.auctionEndAt,
              isEnded: lot.ended || (() => {
                const endAt = parseApiDateTime(auction.auctionEndAt);
                return endAt ? new Date() > endAt : false;
              })(), // 개별 LOT의 ended 상태 또는 경매 전체 종료 상태
              liveStartPrice: getHighestPreBidPrice((auction as any).preBids || [], lot.index) || lot.startPrice || 0, // preBids 최고가 우선, 없으면 시작가
              startPrice: lot.startPrice || 0,
              liveEndTime: auction.auctionEndAt,
              startingPrice: lot.startPrice?.toLocaleString(),
              likedUserIndexes: lot.likedUserIndexes || [], // 좋아요한 사용자 인덱스 배열 추가
              // LOT 코드 정보 추가
              lotCode: lot.lotCode,
              code: lot.code,
              // 원본 API 데이터 포함 (개별 LOT의 ended 상태 포함)
              apiData: {
                ...lot,
                auction: auction
              }
            }));
            setFavoriteItems(convertedItems);
          }
        } else {
          devError('Failed to load auction data:', response.data);
          navigate('/');
        }
      } catch (error) {
        devError('Error loading auction data:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    // 로그인 상태와 무관하게 경매 데이터 로드
    loadAuctionData();
  }, [id, navigate]);

  // 초기 스크롤 실행 여부를 추적하는 상태
  const [hasInitialScrolled, setHasInitialScrolled] = useState(false);

  // 선택된 lot으로 스크롤하는 useEffect 추가 (초기 마운트 시에만)
  useEffect(() => {
    // 이미 초기 스크롤을 실행했다면 더 이상 실행하지 않음
    if (hasInitialScrolled) return;
    
    // 데이터 로딩이 완료되고 location.state에 selectedLotId가 있는 경우
    const selectedLotId = location.state?.selectedLotId;
    
    if (!isLoading && selectedLotId && favoriteItems.length > 0) {
      // 약간의 딜레이 후 스크롤 (DOM 렌더링 완료 대기)
      const timer = setTimeout(() => {
        const targetElement = lotCardRefs.current[selectedLotId];
        if (targetElement) {
          // scroll-margin-top을 이용해 상단 여백을 확보한 상태에서 스크롤
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });

          // 스크롤 후 하이라이트 효과 (옵션)
          targetElement.style.transform = 'scale(1.02)';
          targetElement.style.transition = 'transform 0.3s ease';
          
          setTimeout(() => {
            targetElement.style.transform = '';
          }, 1000);
          
          // 초기 스크롤 완료 표시
          setHasInitialScrolled(true);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, favoriteItems, location.state?.selectedLotId, hasInitialScrolled]);

  // 로딩 중인 경우만 체크 (로그인 상태와 무관하게 접근 허용)
  if (isLoading) {
    return (
      <MainLayout currentPage="home">
        <CenteredLayout>
          <ProjectAppbar 
            currentPage="home"
            title="LOT 내역"
            showBackButton={true}
            onBack={() => navigate(-1)}
          />
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 120px)', /* 앱바 높이를 제외한 전체 화면 높이 */
            width: '100%'
          }}>
            <DataLoader />
          </div>
        </CenteredLayout>
      </MainLayout>
    );
  }

  // 데이터가 없으면 아무것도 렌더링하지 않음
  if (!auctionData) {
    return null;
  }

  // 편의상 변수로 추출
  const auction = auctionData.name || '';
  const auctionHouse = auctionData.auctioneer?.name || '';

  const handleFavoriteToggle = async (e: React.MouseEvent, lotId: number, currentLikedUserIndexes?: number[]) => {
    e.stopPropagation();
    
    // 로그인 상태 확인
    if (!user?.index) {
      devLog('사용자가 로그인하지 않았습니다. 로그인 페이지로 이동합니다.');
      navigate('/login');
      return;
    }

    try {
      devLog('즐겨찾기 토글 시작:', { lotId, currentLikedUserIndexes, userIndex: user.index });
      
      // API 호출
      const response = await likeLot(lotId);
      devLog('likeLot API 응답:', response);
      
      // 현재 사용자가 좋아요했는지 확인
      const wasLiked = currentLikedUserIndexes ? currentLikedUserIndexes.includes(user.index) : false;
      
      // 새로운 likedUserIndexes 계산
      let newLikedUserIndexes: number[] = [];
      if (wasLiked) {
        // 좋아요 취소
        newLikedUserIndexes = currentLikedUserIndexes ? currentLikedUserIndexes.filter(index => index !== user.index) : [];
      } else {
        // 좋아요 추가
        newLikedUserIndexes = currentLikedUserIndexes ? [...currentLikedUserIndexes, user.index] : [user.index];
      }
      
      devLog('좋아요 상태 업데이트:', { wasLiked, newLikedUserIndexes });
      
      // UI 상태 업데이트
      setFavoriteItems(prev => 
        prev.map(item => 
          item.id === lotId
            ? { ...item, likedUserIndexes: newLikedUserIndexes }
            : item
        )
      );
      
      // favoriteManager를 통한 다른 컴포넌트 업데이트
      if (favoriteManager) {
        const updateEvent = {
          lotId,
          isLiked: !wasLiked,
          likedUserIndexes: newLikedUserIndexes
        };
        
        // favoriteManager의 updateCallbacks 직접 호출
        if (favoriteManager.onFavoriteUpdate) {
          favoriteManager.onFavoriteUpdate(updateEvent);
        }
      }
      
    } catch (error) {
      devError('즐겨찾기 처리 중 오류:', error);
    }
  };

  // 카드 클릭 핸들러
  const handleCardClick = (item: FavoriteItem) => {
    const auctionStatus = getAuctionStatus(item);
    const auction = (item as any).apiData?.auction;
    
    // 쇼핑 모드일 때는 before-submission과 before-live 상태에서 아무것도 하지 않음
    if (isShoppingMode() ) {
      return;
    }
    
    if (auctionStatus === 'before-submission') {
      // 사전 입찰 전: 모달 표시
      const preBidStartTime = parseApiDateTime(auction.preBidStartAt) || new Date(auction.preBidStartAt);
      const startTimeMessage = formatDateToLocalString(preBidStartTime, lang);
      setModalState({
        isOpen: true,
        title: texts.auctionNotStarted,
        message: lang === 'ko' 
          ? `${startTimeMessage}부터 사전입찰이 시작됩니다`
          : `Pre-bidding starts from ${startTimeMessage}`,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'before-live') {
      // 사전입찰 종료 후 라이브 시작 전: "라이브 경매 시작전입니다" 모달
      const auctionStartTime = parseApiDateTime(auction.auctionStartAt) || new Date(auction.auctionStartAt);
      const startTimeMessage = formatDateToLocalString(auctionStartTime, lang);
      setModalState({
        isOpen: true,
        title: lang === 'ko' ? '라이브 경매 시작전입니다' : 'Live Auction Not Started',
        message: lang === 'ko' 
          ? `${startTimeMessage}부터 라이브 경매가 시작됩니다` 
          : `Live auction starts from ${startTimeMessage}`,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'ended') {
      // 경매 종료: 모달 표시
      setModalState({
        isOpen: true,
        title: texts.auctionEndedTitle,
        message: texts.auctionEndedMessage,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'submission-period') {
      // 사전입찰 기간: 사전입찰 페이지로 이동
      const lotId = (item as any).apiData?.index || item.id;
      navigate(`/pre-bid/${lotId}`, { state: { selectedItem: item } });
    } else if (auctionStatus === 'live') {
      // 라이브 중: 라이브 페이지로 이동
      navigate('/live-bid', { state: { selectedItem: item } });
    }
  };

  // 모달 닫기 함수
  const closeModal = () => {
    setModalState({
      isOpen: false,
      title: '',
      message: '',
      buttonText: ''
    });
  };

  const renderFavoriteCard = (item: FavoriteItem) => {
    const auctionStatus = getAuctionStatus(item);
    const userHasPreBid = hasUserPreBid(item);
    const userIsTopPreBidder = auctionStatus !== 'ended' && isUserTopPreBidder(item);
    const userIsSecondPreBidder = auctionStatus !== 'ended' && isUserSecondPreBidder(item);
    
    // FavoriteItem을 LikedLotData 타입으로 변환
    const transformedItem: any = {
      index: (item as any).apiData?.index || item.id,
      lotCode: (item as any).lotCode,
      code: (item as any).code,
      name: item.lotName,
      description: '',
      images: [item.imageUrl],
      startPrice: item.liveStartPrice, // 라이브시작가 (preBids 최고가)
      originalStartPrice: item.startPrice, // 원래 시작가
      liveStartPrice: item.liveStartPrice, // 라이브시작가 명시적 추가
      exchangeRate: exchangeRate,
      startPriceUsd: item.liveStartPrice / exchangeRate, // 라이브시작가 기준
      recommendNo: 0,
      likedUserIndexes: item.likedUserIndexes || [],
      likeCount: (item.likedUserIndexes || []).length,
      createBy: '',
      updateBy: '',
      createAt: '',
      updateAt: '',
      category: {
        code: '',
        name: '',
        krName: ''
      },
      subCategory: {
        code: '',
        name: '',
        krName: ''
      },
      auctioneer: {
        index: item.auctioneerIndex,
        name: item.auctionHouse,
        description: '',
        profilePic: ''
      },
      auction: (item as any).apiData?.auction ? {
        index: (item as any).apiData.auction.index,
        name: (item as any).apiData.auction.name,
        auctionStartAt: (item as any).apiData.auction.auctionStartAt,
        auctionEndAt: (item as any).apiData.auction.auctionEndAt,
        preBidStartAt: (item as any).apiData.auction.preBidStartAt,
        preBidEndAt: (item as any).apiData.auction.preBidEndAt
      } : null
    };

    // 간단한 formatLiveTime 함수 (라이브 시간 포맷팅용)
    const simpleFormatLiveTime = (liveTime: string, language: 'ko' | 'en') => {
      return formatLiveEndTime(liveTime, language);
    };

    // 쇼핑 모드일 때 ShoppingCard 사용
    if (isShoppingMode()) {
      return (
        <LotCardWrapper 
          key={item.id}
          ref={(el) => { lotCardRefs.current[item.id] = el; }}
        >
          <ShoppingCard
            item={transformedItem}
            texts={{
              purchase: texts.purchase,
              startPrice: texts.startPrice
            }}
            exchangeRate={exchangeRate}
            formatPrice={formatPrice}
            formatLiveEndTime={formatLiveEndTime}
            onCardClick={() => handleCardClick(item)}
            onFavoriteToggle={(e, index) => handleFavoriteToggle(e, item.id, item.likedUserIndexes)}
          />
        </LotCardWrapper>
      );
    }

    // 일반 모드에서는 기존 LotCard 사용
    return (
      <LotCardWrapper 
        key={item.id}
        ref={(el) => { lotCardRefs.current[item.id] = el; }}
      >
        <LotCard
          item={transformedItem}
          auctionStatus={auctionStatus}
          texts={{
            preSubmission: texts.preSubmission,
            liveView: texts.liveView,
            liveBid: texts.liveBid,
            auctionEnded: texts.auctionEnded,
            liveStartPrice: texts.liveStartPrice,
            startPrice: texts.startPrice,
            cancelBid: texts.cancelBid
          }}
          exchangeRate={exchangeRate}
          formatLiveTime={simpleFormatLiveTime}
          formatPrice={formatPrice}
          formatLiveEndTime={formatLiveEndTime}
          onCardClick={() => {
            // 사전입찰을 한 경우이고 사전입찰 기간이면 카드 클릭 시 일반 동작 수행
            handleCardClick(item);
          }}
          onFavoriteToggle={(e, index) => handleFavoriteToggle(e, item.id, item.likedUserIndexes)}
          // 사전입찰 취소 관련 props 추가
          isBidHistory={userHasPreBid && auctionStatus === 'submission-period'}
          onCancelBid={handleCancelBid}
          // 최고 사전 입찰자 배지 표시 여부 및 라벨
          isTopPreBidder={userIsTopPreBidder}
          topPreBidderLabel={texts.topPreBidder}
          isSecondPreBidder={userIsSecondPreBidder}
          secondPreBidderLabel={texts.secondPreBidder}
        />
      </LotCardWrapper>
    );
  };

  const renderContent = () => {
    if (favoriteItems.length === 0) {
      return (
        <NoDataContainer 
          message="LOT 내역이 없습니다"
          imageSize={80}
        />
      );
    }

    // 라이브보기 버튼 표시 여부 확인
    // 하나라도 라이브 상태인 경우에만 표시 (auctionStartAt 이후)
    const shouldShowLiveButton = favoriteItems.some(item => {
      const status = getAuctionStatus(item);
      return status === 'live';
    });

    // 라이브 상태의 첫 번째 아이템 찾기
    const liveItem = favoriteItems.find(item => {
      const status = getAuctionStatus(item);
      return status === 'live';
    });

    return (
      <>
        {!isShoppingMode() && shouldShowLiveButton && (
          <MobileLiveButtonContainer>
            <LiveViewButton onClick={() => navigate('/live-bid', { state: { selectedItem: liveItem } })}>
              {texts.liveView}
            </LiveViewButton>
          </MobileLiveButtonContainer>
        )}
        <LotDetailGrid>
          {favoriteItems.map(renderFavoriteCard)}
        </LotDetailGrid>
      </>
    );
  };

  const renderDesktopContent = () => {
    if (favoriteItems.length === 0) {
      return (
        <NoDataContainer 
          message="LOT 내역이 없습니다"
          imageSize={80}
        />
      );
    }

    // 라이브보기 버튼 표시 여부 확인
    // 하나라도 라이브 상태인 경우에만 표시 (auctionStartAt 이후)
    const shouldShowLiveButton = favoriteItems.some(item => {
      const status = getAuctionStatus(item);
      return status === 'live';
    });

    // 라이브 상태의 첫 번째 아이템 찾기
    const liveItem = favoriteItems.find(item => {
      const status = getAuctionStatus(item);
      return status === 'live';
    });

    return (
      <>
        <TitleRow>
          <FormTitle>{auction} - {auctionHouse}</FormTitle>
          {!isShoppingMode() && shouldShowLiveButton && (
            <LiveViewButton onClick={() => navigate('/live-bid', { state: { selectedItem: liveItem } })}>
              {texts.liveView}
            </LiveViewButton>
          )}
        </TitleRow>
        <LotDetailGrid>
          {favoriteItems.map(renderFavoriteCard)}
        </LotDetailGrid>
      </>
    );
  };

  // 모바일 레이아웃
  const MobileLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={`${auction}`}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <LotDetailContainer>
          <ContentContainer>
            {renderContent()}
          </ContentContainer>
        </LotDetailContainer>
        
        {/* 모달 */}
        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          header={modalState.title}
          body={modalState.message}
          footer={
            <PrimaryButton onClick={closeModal}>
              {modalState.buttonText}
            </PrimaryButton>
          }
        />
      </CenteredLayout>
    </MainLayout>
  );

  // 데스크톱 레이아웃
  const DesktopLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={`${auction} - ${auctionHouse}`}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <LotDetailContainer>
          <ContentContainer>
            {renderDesktopContent()}
          </ContentContainer>
        </LotDetailContainer>
        
        {/* 모달 */}
        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          header={modalState.title}
          body={modalState.message}
          footer={
            <PrimaryButton onClick={closeModal}>
              {modalState.buttonText}
            </PrimaryButton>
          }
        />
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <>
      <ResponsiveView
        mobileView={MobileLayout}
        desktopView={DesktopLayout}
      />
      
      {/* 입찰 취소 모달 */}
      <Modal
        isOpen={cancelBidModalState.isOpen}
        onClose={handleCancelBidModalClose}
        header={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalTitle : texts.cancelBidBlockedTitle}
        bgColor="white"
        body={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalMessage : (cancelBidModalState.blockedReason || '')}
        footer={
          cancelBidModalState.mode === 'confirm' ? (
            <ButtonContainer>
              <CancelModalButton onClick={handleCancelBidModalClose}>
                {texts.cancel}
              </CancelModalButton>
              <ConfirmModalButton onClick={handleConfirmCancelBid}>
                {texts.confirmCancel}
              </ConfirmModalButton>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              <ConfirmModalButton onClick={handleCancelBidModalClose}>
                {texts.confirmButton}
              </ConfirmModalButton>
            </ButtonContainer>
          )
        }
      />
    </>
  );
}

export default LotDetail;
